import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, X, CheckCircle, AlertTriangle, FileText, Search, Save, Printer } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const PickupStatus = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState('');

  const [pickupNote, setPickupNote] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [voucherSearch, setVoucherSearch] = useState('');

  // Note selection modal
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [modalSearchKeyword, setModalSearchKeyword] = useState('');

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response = await MainService.StatusInPickupStatus();
        setStatusOptions(Array.isArray(response) ? response : response?.Data || []);
      } catch (err) {
        console.error('Error loading pickup statuses:', err);
        setError('Network Error');
      }
    };
    loadStatuses();
  }, []);

  // ==================== Helpers ====================
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // ==================== Data loading ====================
  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await MainService.PickupNotesInList({
        Skip: 0,
        Take: 100,
        Status: 'CONFIRMED',
      });
      if (response && Array.isArray(response)) {
        setNotes(response);
      } else if (response && response.Data) {
        setNotes(response.Data);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error loading pickup notes:', err);
      setError('Failed to load pickup notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadRows = async (noteId) => {
    if (!noteId) return;
    try {
      setLoading(true);
      const response = await MainService.GetPickupDetails({
        PickUpNoteId: noteId,
        Skip: 0,
        Take: 500,
        Page: 1,
        PageSize: 500,
      });
      const data = Array.isArray(response) ? response : response?.Data || [];
      setRows(data.map((row) => ({ ...row, checked: false })));
      setSelectAll(false);
      setVoucherSearch('');
    } catch (err) {
      console.error('Error loading pickup details:', err);
      setError('Network Error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ==================== Handlers ====================
  const openNoteModal = () => {
    setModalSearchKeyword('');
    loadNotes();
    setShowNoteModal(true);
  };

  const selectNote = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.GetPickupNoteOnlyById(note.Id);
      if (response) {
        setPickupNote(response);
        await loadRows(response.Id);
      }
      setShowNoteModal(false);
    } catch (err) {
      console.error('Error selecting pickup note:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const next = !selectAll;
    setSelectAll(next);
    setRows((prev) => prev.map((row) => ({ ...row, checked: next })));
  };

  const handleToggleRow = (rowId) => {
    setRows((prev) => {
      const updated = prev.map((row) => (row.Id === rowId ? { ...row, checked: !row.checked } : row));
      setSelectAll(updated.length > 0 && updated.every((row) => row.checked));
      return updated;
    });
  };

  const selectRowByVoucher = (voucherValue, { silent = false } = {}) => {
    const searchTerm = String(voucherValue || '').trim();
    if (!searchTerm || rows.length === 0) return false;

    const matched = rows.find(
      (row) => String(row.Order?.VoucherNo ?? '').toLowerCase() === searchTerm.toLowerCase()
    );

    if (!matched) {
      if (!silent) {
        setError(`Voucher No "${searchTerm}" not found`);
      }
      return false;
    }

    setRows((prev) => {
      const updated = prev.map((row) =>
        row.Id === matched.Id ? { ...row, checked: true } : row
      );
      setSelectAll(updated.length > 0 && updated.every((row) => row.checked));
      return updated;
    });

    setVoucherSearch('');
    setSuccess(`Selected voucher ${matched.Order?.VoucherNo || searchTerm}`);

    setTimeout(() => {
      const rowElement = document.getElementById(`pickup-status-row-${matched.Id}`);
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        rowElement.classList.add('bg-yellow-100');
        setTimeout(() => {
          rowElement.classList.remove('bg-yellow-100');
        }, 1200);
      }
    }, 50);

    return true;
  };

  // Auto-select once typing/scanning finishes (no Enter needed)
  useEffect(() => {
    const term = voucherSearch.trim();
    if (!term || rows.length === 0) return undefined;

    const timer = setTimeout(() => {
      selectRowByVoucher(term, { silent: true });
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherSearch, rows]);

  const handleVoucherSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      selectRowByVoucher(voucherSearch);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatusId) {
      setError('Select Pickup Status');
      return;
    }
    const selectedRows = rows.filter((row) => row.checked);
    if (selectedRows.length === 0) {
      setError('Please Select Orders');
      return;
    }

    const pickupNoteDetails = selectedRows.map((row) => ({
      PickUpNoteId: pickupNote?.Id,
      Id: row.Id,
      OrderId: row.OrderId,
      StatusId: Number(selectedStatusId),
    }));

    try {
      setLoading(true);
      const response = await MainService.UpdateOrderPickUpStatus(pickupNoteDetails);
      if (response === true || response === 'true') {
        setSuccess('Success');
        setSelectedStatusId('');
        setSelectAll(false);
        await loadRows(pickupNote.Id);
      } else {
        setError('Something went wrong');
      }
    } catch (err) {
      console.error('Error updating pickup status:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (id) => {
    try {
      const response = await MainService.PrintPickUpStatus(id);
      const filePath = typeof response === 'string' ? response : response?.data || response;
      if (filePath) {
        const fullUrl = String(filePath).startsWith('http')
          ? filePath
          : `${getApiBaseUrl()}ReportFiles/${filePath}`;
        window.open(fullUrl, '_blank');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError('Print Error');
    }
  };

  const handleSavePickupStatus = async (mode) => {
    if (!pickupNote?.Id) {
      setError('Select Pickup Note');
      return;
    }
    // All orders must be moved out of the "Pickup Ticket" (PKT) processing state.
    const hasPending = rows.some((row) => row.Status?.Keyword === 'PKT');
    if (hasPending) {
      setError('Some Orders Status Not Updated !');
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.SavePickupNoteStatus(pickupNote);
      if (response?.Status === 'Success') {
        setSuccess('Success');
        if (mode === 2 && response.PickupNoteId) {
          setSuccess('Printing');
          await handlePrint(response.PickupNoteId);
        }
        setPickupNote(null);
        setRows([]);
        setSelectAll(false);
        setSelectedStatusId('');
        setVoucherSearch('');
      } else {
        setError(response?.Status || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error saving pickup status:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (pickupNote?.Id) {
      loadRows(pickupNote.Id);
    }
  };

  const filteredNotes = notes.filter((note) => {
    if (!modalSearchKeyword) return true;
    const kw = modalSearchKeyword.toLowerCase();
    return (
      (note.RefNo || '').toLowerCase().includes(kw) ||
      ((note.Driver || {}).Name || '').toLowerCase().includes(kw) ||
      (note.Status || '').toLowerCase().includes(kw)
    );
  });

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'pickupStatus')}</h1>
            <p className="text-sm text-gray-500 mt-1">Update pickup status for a confirmed pickup note</p>
          </div>
          <button
            onClick={openNoteModal}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Select Pickup Note
          </button>
        </div>
      </div>

      {/* Pickup Note info + controls */}
      {pickupNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
            <span><b>Ref No:</b> {pickupNote.RefNo || '-'}</span>
            <span><b>Vehicle:</b> {pickupNote.Vehicle?.Number || '-'}</span>
            <span><b>Driver:</b> {pickupNote.Driver?.Name || '-'}</span>
            <span><b>Date:</b> {formatDate(pickupNote.Date)}</span>
            <span><b>Status:</b> {pickupNote.Status || '-'}</span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px] max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scan or search voucher no</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={voucherSearch}
                  onChange={(e) => setVoucherSearch(e.target.value)}
                  onKeyDown={handleVoucherSearchKeyDown}
                  placeholder="Scan or search voucher no"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Status</label>
              <select
                value={selectedStatusId}
                onChange={(e) => setSelectedStatusId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent min-w-[200px]"
              >
                <option value="">Select status</option>
                {statusOptions.map((s) => (
                  <option key={s.Id} value={s.Id}>{s.Name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleUpdateStatus}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              Update Status
            </button>
          </div>
        </div>
      )}

      {/* Grid */}
      {pickupNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full min-w-[1200px] text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="w-4 h-4" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Voucher No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Bill</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pickup Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Emirate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[150px]">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Store Ref</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">Loading orders...</p>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-gray-500">No orders found</td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const order = row.Order || {};
                    return (
                      <tr
                        key={row.Id}
                        id={`pickup-status-row-${row.Id}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={row.checked || false}
                            onChange={() => handleToggleRow(row.Id)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">{order.VoucherNo || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.ClientBillNo || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(order.PickupDate)}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.Client?.Name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.ClientAddress?.Emirate?.Name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.ClientAddress?.Location?.Name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.Customer?.Name || '-'}</td>
                        <td className="px-4 py-3 min-w-[150px]">
                          <span
                            className="px-2 py-1 text-xs font-medium rounded-full"
                            style={{
                              backgroundColor: row.Status?.Color ? `${row.Status.Color}20` : '#e5e7eb',
                              color: row.Status?.Color || '#374151',
                            }}
                          >
                            {row.Status?.Name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{order.StoreRefNo || '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer actions */}
          <div className="flex flex-wrap items-center justify-center gap-3 p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => handleSavePickupStatus(1)}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Pickup Status
            </button>
            <button
              onClick={() => handleSavePickupStatus(2)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Save &amp; Print Pickup Status
            </button>
          </div>
        </div>
      )}

      {!pickupNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pickup Note Selected</h3>
          <p className="text-sm text-gray-500">Select a confirmed pickup note to update its status</p>
        </div>
      )}

      {/* Loading overlay */}
      {loading && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <span className="text-gray-700 font-medium">Loading...</span>
          </div>
        </div>,
        document.body
      )}

      {/* Select Pickup Note Modal */}
      {showNoteModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Select Pickup Note</h2>
                <button onClick={() => setShowNoteModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No, Driver, or Status..."
                  value={modalSearchKeyword}
                  onChange={(e) => setModalSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No pickup notes found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredNotes.map((note, index) => (
                        <tr key={note.Id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => selectNote(note)}>
                          <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(note.Date)}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">{note.RefNo || '-'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {note.Status || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{(note.Driver || {}).Name || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {note.Vehicle ? `${note.Vehicle.Number || ''}${note.Vehicle.Model ? `, ${note.Vehicle.Model}` : ''}` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                selectNote(note);
                              }}
                              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PickupStatus;
